import { buildSystemCommandPayload, type SystemCommandPayload } from '../command-payload';

export type SlashCommand = { cmd: string; desc: string };

export function getAvailableSlashCommands(commands: SlashCommand[], agent: string, filter = ''): SlashCommand[] {
  return commands
    .filter((command) => agent !== 'opencode' || command.cmd !== '/compact')
    .filter((command) => command.cmd.startsWith(filter) || command.desc.includes(filter.slice(1)))
    .sort((left, right) => (right.cmd === filter ? 1 : 0) - (left.cmd === filter ? 1 : 0));
}

type CommandPickerControllerOptions = {
  cmdMenu: HTMLElement;
  commandToggle?: HTMLElement | null;
  chatMain: Element | null;
  slashCommands: SlashCommand[];
  claudeModelEntries: any[];
  modePickerOptions: any[];
  escapeHtml: (value: any) => string;
  getCurrentAgent: () => string;
  getCurrentSessionId: () => string | null;
  getCurrentMode: () => string;
  sendMessagePayload: (payload: SystemCommandPayload) => void;
  onModeSelected: (value: string) => void;
};

export function createCommandPickerController(options: CommandPickerControllerOptions) {
  let cmdMenuIndex = -1;
  let cmdMenuDelegated = false;

  function sendSystemCommand(text: string) {
    options.sendMessagePayload(buildSystemCommandPayload(text, {
      sessionId: options.getCurrentSessionId(),
      mode: options.getCurrentMode(),
      agent: options.getCurrentAgent(),
    }));
  }

  function hideOptionPicker() {
    const picker = document.getElementById('option-picker') as any;
    if (picker) {
      if (picker._keyHandler) document.removeEventListener('keydown', picker._keyHandler);
      picker.remove();
    }
    document.removeEventListener('click', pickerOutsideClick);
    document.removeEventListener('keydown', pickerEscape);
  }

  function pickerOutsideClick(e: Event) {
    const picker = document.getElementById('option-picker');
    if (picker && !picker.contains(e.target as Node)) {
      hideOptionPicker();
    }
  }

  function pickerEscape(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      hideOptionPicker();
    }
  }

  function showOptionPicker(title: string, pickerOptions: any[], currentValue: string, onSelect: (value: string) => void) {
    hideOptionPicker();

    const picker = document.createElement('div');
    picker.className = 'option-picker';
    picker.id = 'option-picker';
    picker.innerHTML = `
      <div class="option-picker-title">${options.escapeHtml(title)}</div>
      <div class="option-picker-list">
        ${pickerOptions.map((opt) => `
          <div class="option-picker-item${opt.value === currentValue ? ' active' : ''}" data-value="${opt.value}">
            <div class="option-picker-item-info">
              <div class="option-picker-item-label">${options.escapeHtml(opt.label)}</div>
              <div class="option-picker-item-desc">${options.escapeHtml(opt.desc)}</div>
            </div>
            ${opt.value === currentValue ? '<span class="option-picker-item-check">✓</span>' : ''}
          </div>
        `).join('')}
      </div>
    `;

    options.chatMain?.appendChild(picker);
    const activeItem = picker.querySelector('.option-picker-item.active');
    if (activeItem) requestAnimationFrame(() => activeItem.scrollIntoView({ block: 'nearest' }));

    Array.from(picker.querySelectorAll('.option-picker-item') as NodeListOf<HTMLElement>).forEach((el) => {
      el.addEventListener('click', () => {
        onSelect(el.dataset.value || '');
        hideOptionPicker();
      });
    });

    setTimeout(() => {
      document.addEventListener('click', pickerOutsideClick);
    }, 0);
    document.addEventListener('keydown', pickerEscape);
  }

  function showModelPicker() {
    sendSystemCommand('/model');
  }

  function showClaudeModelPicker(menuEntries: any[], models: any, currentAlias: string, currentFull: string, onSelect?: (value: string) => void) {
    hideOptionPicker();

    const picker = document.createElement('div') as any;
    picker.className = 'option-picker model-picker-claude';
    picker.id = 'option-picker';

    let activeAlias = 'default';
    if (currentFull) {
      const fullToAlias: Record<string, string> = {};
      if (models.opus) fullToAlias[models.opus] = 'opus';
      if (models.sonnet) fullToAlias[models.sonnet] = 'sonnet';
      if (models.haiku) fullToAlias[models.haiku] = 'haiku';
      activeAlias = fullToAlias[currentFull] || currentAlias || currentFull;
    } else if (currentAlias && currentAlias !== 'default' && currentAlias !== '') {
      activeAlias = currentAlias;
    }

    const entries = (Array.isArray(menuEntries) && menuEntries.length > 0)
      ? menuEntries.map((entry) => Object.assign({}, entry, { value: entry.value || entry.alias }))
      : options.claudeModelEntries.map((entry) => Object.assign({}, entry));

    const isStandard = entries.some((e) => e.alias === activeAlias || (currentFull && (e.value || e.alias) === currentFull));
    if (!isStandard && currentFull) {
      entries.push({
        alias: currentAlias || currentFull,
        value: currentFull,
        label: currentAlias || currentFull,
        desc: currentFull,
        pricing: '',
      });
    }

    let focusIdx = entries.findIndex((e) => e.alias === activeAlias || (currentFull && (e.value || e.alias) === currentFull));
    if (focusIdx < 0) focusIdx = 0;

    const itemsHtml = entries.map((e, i) => {
      const itemValue = e.value || e.alias;
      const isCurrent = e.alias === activeAlias || (currentFull && itemValue === currentFull);
      const isFocused = i === focusIdx;
      const descText = e.desc + (e.pricing ? ' \u00b7 ' + e.pricing : '');
      return `
        <div class="option-picker-item${isCurrent ? ' active' : ''}${isFocused ? ' focused' : ''}" data-value="${options.escapeHtml(itemValue)}" data-index="${i}">
          <span class="mp-cursor">${isFocused ? '\u276f' : '\u2003'}</span>
          <span class="mp-num">${i + 1}.</span>
          <div class="option-picker-item-info">
            <div class="option-picker-item-label">${options.escapeHtml(e.label)}${isCurrent ? ' <span class="mp-check">\u2714</span>' : ''}</div>
            <div class="option-picker-item-desc">${options.escapeHtml(descText)}</div>
          </div>
        </div>
      `;
    }).join('');

    picker.innerHTML = `
      <div class="mp-header">
        <div class="option-picker-title">选择模型</div>
        <div class="mp-subtitle">切换当前会话使用的 Claude 模型。<br>如果要指定其他或旧版模型名，请使用 <code>--model</code>。</div>
      </div>
      <div class="mp-items">${itemsHtml}</div>
      <div class="mp-custom">
        <button type="button" id="model-custom-btn" class="mp-custom-input">输入自定义模型 ID</button>
      </div>
      <div class="mp-hint">点击自定义模型后使用系统弹窗输入 · Esc 关闭</div>
    `;

    options.chatMain?.appendChild(picker);
    const itemsContainer = picker.querySelector('.mp-items');

    function scrollFocusedItemIntoView() {
      const activeEl = picker.querySelector(`.option-picker-item[data-index="${focusIdx}"]`);
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }

    function updateFocus(newIdx: number) {
      focusIdx = newIdx;
      picker.querySelectorAll('.option-picker-item').forEach((el: any, i: number) => {
        el.classList.toggle('focused', i === focusIdx);
        const cur = el.querySelector('.mp-cursor');
        if (cur) cur.textContent = i === focusIdx ? '\u276f' : '\u2003';
      });
      if (itemsContainer) scrollFocusedItemIntoView();
    }

    // 使用系统 JS 弹窗收集自定义模型参数；取消或空值都不发送请求。
    function promptCustomModel() {
      const value = window.prompt('输入自定义模型 ID', currentFull || '');
      const normalizedValue = String(value || '').trim();
      if (!normalizedValue) return;
      if (onSelect) onSelect(normalizedValue);
      else sendSystemCommand(`/model ${normalizedValue}`);
      hideOptionPicker();
    }

    picker.querySelector('#model-custom-btn')?.addEventListener('click', promptCustomModel);

    Array.from(picker.querySelectorAll('.option-picker-item') as NodeListOf<HTMLElement>).forEach((el) => {
      el.addEventListener('click', () => {
        if (onSelect) onSelect(el.dataset.value || '');
        else sendSystemCommand(`/model ${el.dataset.value}`);
        hideOptionPicker();
      });
      el.addEventListener('mouseenter', () => updateFocus(parseInt(el.dataset.index || '0', 10)));
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        updateFocus((focusIdx + 1) % entries.length);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        updateFocus((focusIdx - 1 + entries.length) % entries.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const entry = entries[focusIdx];
        if (onSelect) onSelect(entry.value || entry.alias);
        else sendSystemCommand(`/model ${entry.value || entry.alias}`);
        hideOptionPicker();
      } else if (e.key === 'Escape') {
        hideOptionPicker();
      } else if (e.key >= '1' && e.key <= '9') {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < entries.length) {
          e.preventDefault();
          updateFocus(idx);
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    picker._keyHandler = handleKeyDown;

    requestAnimationFrame(() => {
      if (itemsContainer) scrollFocusedItemIntoView();
    });

    setTimeout(() => {
      document.addEventListener('click', pickerOutsideClick);
    }, 0);
  }

  function showModePicker() {
    showOptionPicker('选择权限模式', options.modePickerOptions, options.getCurrentMode(), (value) => {
      options.onModeSelected(value);
    });
  }

  function applySlashCommandSelection(cmd: string) {
    if (!cmd) return;
    if (cmd === '/model') {
      hideCmdMenu();
      showModelPicker();
      return;
    }
    if (cmd === '/mode') {
      hideCmdMenu();
      showModePicker();
      return;
    }
    sendSystemCommand(cmd);
    hideCmdMenu();
  }

  function ensureCmdMenuDelegation() {
    if (cmdMenuDelegated) return;
    cmdMenuDelegated = true;
    options.cmdMenu.addEventListener('click', (event) => {
      const cmdItem = event.target instanceof Element ? event.target.closest('.cmd-item') : null;
      if (!cmdItem || !options.cmdMenu.contains(cmdItem)) return;
      applySlashCommandSelection((cmdItem as HTMLElement).dataset.cmd || '');
    });
  }

  function showCmdMenu(filter: string) {
    const filtered = getAvailableSlashCommands(options.slashCommands, options.getCurrentAgent(), filter);
    if (filtered.length === 0) {
      hideCmdMenu();
      return;
    }
    cmdMenuIndex = 0;
    options.cmdMenu.innerHTML = `
      <div class="quick-message-panel-header">
        <span class="quick-message-panel-title">指令</span>
      </div>
      <div class="quick-message-list command-panel-list">
        ${filtered.map((c, i) => `
          <button type="button" class="quick-message-chip cmd-item${i === 0 ? ' active' : ''}" data-cmd="${options.escapeHtml(c.cmd)}">
            <span class="cmd-item-cmd">${options.escapeHtml(c.cmd)}</span>
            <span class="cmd-item-desc">${options.escapeHtml(c.desc)}</span>
          </button>
        `).join('')}
      </div>
    `;
    (options.cmdMenu as HTMLElement).hidden = false;
    options.commandToggle?.setAttribute('aria-expanded', 'true');
    ensureCmdMenuDelegation();
  }

  function hideCmdMenu() {
    (options.cmdMenu as HTMLElement).hidden = true;
    options.commandToggle?.setAttribute('aria-expanded', 'false');
    cmdMenuIndex = -1;
  }

  function navigateCmdMenu(direction: number) {
    const items = options.cmdMenu.querySelectorAll('.cmd-item');
    if (items.length === 0) return;
    (items[cmdMenuIndex] as HTMLElement | undefined)?.classList.remove('active');
    cmdMenuIndex = (cmdMenuIndex + direction + items.length) % items.length;
    (items[cmdMenuIndex] as HTMLElement | undefined)?.classList.add('active');
  }

  function selectCmdMenuItem() {
    const items = options.cmdMenu.querySelectorAll<HTMLElement>('.cmd-item');
    if (cmdMenuIndex >= 0 && items[cmdMenuIndex]) {
      applySlashCommandSelection(items[cmdMenuIndex].dataset.cmd || '');
    }
  }

  return {
    showCmdMenu,
    hideCmdMenu,
    navigateCmdMenu,
    selectCmdMenuItem,
    showOptionPicker,
    hideOptionPicker,
    showModelPicker,
    showClaudeModelPicker,
    showModePicker,
  };
}
